import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page">
      <header className="legal-page__header">
        <div className="legal-page__container">
          <Link to="/" className="legal-page__logo">
            <img src="/ralli-logo.svg" alt="RalliPB" className="legal-page__logo-img" />
            <span>RalliPB</span>
          </Link>
        </div>
      </header>

      <main className="legal-page__content">
        <div className="legal-page__container">
          <h1>Privacy Policy</h1>
          <p className="legal-page__updated">Last updated: June 26, 2026</p>

          <section>
            <h2>1. Introduction</h2>
            <p>
              Welcome to RalliPB ("we," "our," or "us"). We are committed to protecting your personal
              information and your right to privacy. This Privacy Policy explains how we collect, use,
              disclose, and safeguard your information when you use our mobile application and website
              (collectively, the "Platform").
            </p>
            <p>
              Please read this Privacy Policy carefully. By using RalliPB, you agree to the collection
              and use of information in accordance with this policy.
            </p>
          </section>

          <section>
            <h2>2. Information We Collect</h2>

            <h3>2.1 Personal Information You Provide</h3>
            <p>We collect information that you voluntarily provide when using our Platform, including:</p>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, password, and authentication details when you create an account</li>
              <li><strong>Profile Information:</strong> Profile photo, city, state, zip code, home court, skill level, playing preferences, and bio</li>
              <li><strong>Skill and Rating Information:</strong> Self-selected skill level and optional rating information available on your account</li>
              <li><strong>Community Activity:</strong> Friend requests, group membership, court check-ins, join intents, scheduled sessions, RSVPs, and messages</li>
              <li><strong>Support and Feedback:</strong> Feedback form submissions, support messages, ratings, categories, comments, and optional screenshots</li>
            </ul>

            <h3>2.2 Information Collected Automatically</h3>
            <p>When you use our Platform, we may automatically collect:</p>
            <ul>
              <li><strong>Device Information:</strong> Device type, operating system, app version, and device identifiers</li>
              <li><strong>Location Data:</strong> With your permission, location data used for nearby courts, distance sorting, check-ins, and optional geofencing features</li>
              <li><strong>Usage Data:</strong> How you interact with the Platform, including features used, time spent, and actions taken</li>
              <li><strong>Push Notification Tokens:</strong> Tokens used to send notifications about court activity, friend requests, messages, session invites, and updates</li>
            </ul>
          </section>

          <section>
            <h2>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul>
              <li>Provide, maintain, and improve the Platform</li>
              <li>Enable account creation, authentication, and profile management</li>
              <li>Help you find courts and connect with other pickleball players</li>
              <li>Show nearby courts, friend check-ins, join intents, and scheduled sessions</li>
              <li>Display your self-rating and optional rating information on your profile</li>
              <li>Send notifications about court activity, friend requests, messages, invites, and app updates</li>
              <li>Respond to support requests and beta feedback</li>
              <li>Detect, prevent, and address technical issues, abuse, and security threats</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2>4. Information Sharing</h2>
            <p>We may share your information in the following circumstances:</p>
            <ul>
              <li><strong>With Other Users:</strong> Public profile details, self-rating, friend status, group context, court check-ins, join intents, and scheduled session details may be visible to other RalliPB users depending on the feature</li>
              <li><strong>With Service Providers:</strong> We share information with vendors who help us operate the Platform, such as hosting, storage, database, authentication, analytics, and notification services</li>
              <li><strong>With Rating Services:</strong> If you choose to connect or display rating information from a third-party rating service, data may be exchanged as needed to provide that feature</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law or to protect our rights, users, and the Platform</li>
              <li><strong>Business Transfers:</strong> We may disclose information in connection with a merger, acquisition, financing, or sale of assets</li>
            </ul>
          </section>

          <section>
            <h2>5. Data Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect your personal
              information. However, no method of transmission over the Internet or electronic storage
              is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2>6. Your Rights and Choices</h2>
            <p>You have the right to:</p>
            <ul>
              <li>Access, update, or delete your account information</li>
              <li>Update your profile, self-rating, location settings, and notification permissions</li>
              <li>Disable location services in your device settings</li>
              <li>Opt out of push notifications in your device settings</li>
              <li>Request a copy of your data</li>
              <li>Request deletion of your account and associated data</li>
            </ul>
          </section>

          <section>
            <h2>7. Children's Privacy</h2>
            <p>
              RalliPB is not intended for children under 13 years of age. We do not knowingly collect
              personal information from children under 13. If we learn that we have collected personal
              information from a child under 13, we will delete that information promptly.
            </p>
          </section>

          <section>
            <h2>8. Third-Party Services</h2>
            <p>
              Our Platform may use or link to third-party services, including hosting, authentication,
              storage, notification, analytics, mapping, and optional rating services. We are not
              responsible for the privacy practices of these third parties. We encourage you to read
              the privacy policies of any third-party services you use.
            </p>
          </section>

          <section>
            <h2>9. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any changes
              by posting the new Privacy Policy on this page and updating the "Last updated" date.
              Your continued use of the Platform after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2>10. Contact Us</h2>
            <p>
              If you have questions about this Privacy Policy or our privacy practices, please contact us at:
            </p>
            <p>
              <strong>Email:</strong> <a href="mailto:privacy@ralli.app">privacy@ralli.app</a><br />
              <strong>Support:</strong> <Link to="/support">RalliPB Support</Link><br />
              <strong>Address:</strong> RalliPB, Inc.
            </p>
          </section>
        </div>
      </main>

      <footer className="legal-page__footer">
        <div className="legal-page__container">
          <p>&copy; 2026 RalliPB. All rights reserved.</p>
          <div className="legal-page__footer-links">
            <Link to="/privacy">Privacy Policy</Link>
            <Link to="/terms">Terms of Service</Link>
            <Link to="/support">Support</Link>
            <Link to="/">Home</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
